package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document
import org.w3c.dom.HTMLStyleElement

/**
 * Injects raw CSS exactly once via a real `<style>` element appended to `<head>`.
 *
 * Use this instead of Compose HTML's `Style { }` builder for anything written as a raw
 * CSS string. `Style { }`'s trailing lambda is a `CSSRulesHolder` builder, so
 *
 *     Style { """@keyframes foo { ... }""".trimIndent() }
 *
 * is an *unused expression* — it compiles, emits an EMPTY `<style>` tag, and the rules
 * silently never exist. Going through the DOM hands the text to the browser's own CSS
 * engine, which also copes with `@media`, vendor prefixes and multi-comma values that
 * Compose HTML's naive parser drops.
 *
 * [id] must be unique per CSS block; it doubles as the guard against re-injection, so
 * two components sharing an id share one injection (see [InjectSpinKeyframes]).
 */
@Composable
fun InjectStyles(id: String, css: String) {
    LaunchedEffect(id) {
        if (document.getElementById(id) != null) return@LaunchedEffect
        val style = document.createElement("style") as HTMLStyleElement
        style.id = id
        style.textContent = css
        document.head?.appendChild(style)
    }
}

/**
 * `@keyframes spin` — the 360° rotation every loading spinner in the app references via
 * `animation: spin <n>s linear infinite`. Shared id, so whichever spinner mounts first
 * injects it for all of them.
 */
@Composable
fun InjectSpinKeyframes() {
    InjectStyles(
        "ff-keyframes-spin",
        """
        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        """.trimIndent()
    )
}

/**
 * The orbiting-dot keyframes used by the analyzing view. Lives here because both
 * ProgressTracker and the results view on Index need them, and the results view can
 * render without the analyzing view ever mounting.
 */
@Composable
fun InjectOrbKeyframes() {
    InjectStyles(
        "ff-keyframes-orb",
        """
        @keyframes orb1Perimeter {
            0%   { top: 0;    left: 0; }
            25%  { top: 0;    left: 100%; }
            50%  { top: 100%; left: 100%; }
            75%  { top: 100%; left: 0; }
            100% { top: 0;    left: 0; }
        }
        @keyframes orb2Perimeter {
            0%   { top: 100%; left: 100%; }
            25%  { top: 100%; left: 0; }
            50%  { top: 0;    left: 0; }
            75%  { top: 0;    left: 100%; }
            100% { top: 100%; left: 100%; }
        }
        @keyframes orbPulse {
            from { opacity: 0.65; }
            to   { opacity: 0.95; }
        }
        @keyframes orbPulse2 {
            from { opacity: 0.60; }
            to   { opacity: 0.92; }
        }
        """.trimIndent()
    )
}
